import {
  isFeatureAllowedByPricingPlan,
  PricingPlanEnum,
} from "admin/organization/pricing_plan_utils";
import {
  getAnnotationCompoundInformation,
  getAnnotationProto,
  getDataset,
  getDatasetViewConfiguration,
  getEditableMappingInfo,
  getEmptySandboxAnnotationInformation,
  getKeyboardShortcutsConfig,
  getSharingTokenFromUrlParameters,
  getTracingsForAnnotation,
  getUnversionedAnnotationInformation,
  getUserConfiguration,
} from "admin/rest_api";
import ErrorHandling from "libs/error_handling";
import Toast from "libs/toast";
import { point3ToVector3 } from "libs/utils";
import { location } from "libs/window";
import cloneDeep from "lodash-es/cloneDeep";
import extend from "lodash-es/extend";
import first from "lodash-es/first";
import isEmpty from "lodash-es/isEmpty";
import isEqual from "lodash-es/isEqual";
import merge from "lodash-es/merge";
import messages from "messages";
import type {
  APIAnnotation,
  APIAnnotationUserState,
  APICompoundType,
  APIDataLayer,
  APIDataset,
  APISegmentationLayer,
  APITracingStoreAnnotation,
  MutableAPIDataset,
  ServerEditableMapping,
  ServerTracing,
  ServerVolumeTracing,
} from "types/api_types";
import { enforceValidatedDatasetViewConfiguration } from "types/schemas/dataset_view_configuration_defaults";
import type { Mutable } from "types/type_utils";
import constants, { ControlModeEnum, type Vector3 } from "viewer/constants";
import { applyState, getIsNativelyRenderedNamePresent } from "viewer/controller/apply_url_state";
import type { PartialUrlManagerState } from "viewer/controller/url_manager";
import UrlManager, {
  getDatasetNameFromLocation,
  getUpdatedPathnameWithNewDatasetName,
} from "viewer/controller/url_manager";
import {
  determineAllowedModes,
  getDataLayers,
  getDatasetCenter,
  getSegmentationLayers,
  getUnifiedAdditionalCoordinates,
  hasSegmentation,
  isElementClassSupported,
  isSegmentationLayer,
} from "viewer/model/accessors/dataset_accessor";
import { getNullableSkeletonTracing } from "viewer/model/accessors/skeletontracing_accessor";
import { AnnotationTool } from "viewer/model/accessors/tool_accessor";
import { getServerVolumeTracings } from "viewer/model/accessors/volumetracing_accessor";
import {
  batchedAnnotationInitializationAction,
  initializeAnnotationAction,
} from "viewer/model/actions/annotation_actions";
import { setDatasetAction } from "viewer/model/actions/dataset_actions";
import {
  setAdditionalCoordinatesAction,
  setZoomStepAction,
} from "viewer/model/actions/flycam_actions";
import {
  initializeGpuSetupAction,
  initializeSettingsAction,
  setControlModeAction,
  setKeyboardShortcutsConfigAction,
  setViewModeAction,
} from "viewer/model/actions/settings_actions";
import { initializeSkeletonTracingAction } from "viewer/model/actions/skeletontracing_actions";
import { setTaskAction } from "viewer/model/actions/task_actions";
import { setToolAction } from "viewer/model/actions/ui_actions";
import {
  initializeEditableMappingAction,
  initializeVolumeTracingAction,
} from "viewer/model/actions/volumetracing_actions";
import {
  computeDataTexturesSetup,
  getSupportedTextureSpecs,
  validateMinimumRequirements,
} from "viewer/model/bucket_data_handling/data_rendering_logic";
import DataLayer from "viewer/model/data_layer";
import type {
  DatasetConfiguration,
  DatasetLayerConfiguration,
  StoreDataset,
  TraceOrViewCommand,
  UserConfiguration,
} from "viewer/store";
import Store from "viewer/store";
import { initializeKeyboardLayoutMap } from "viewer/view/keyboard_shortcuts/keyboard_layout_utils";
import { getAllDefaultKeyboardShortcuts } from "viewer/view/keyboard_shortcuts/keyboard_shortcut_constants";
import {
  sanitizeKeyboardShortcuts,
  validateShortcutMapText,
} from "viewer/view/keyboard_shortcuts/keyboard_shortcut_persistence";
import type { KeyboardShortcutsMap } from "viewer/view/keyboard_shortcuts/keyboard_shortcut_types";
import { getUserStateForTracing } from "./model/accessors/annotation_accessor";
import { doAllLayersHaveTheSameRotation } from "./model/accessors/dataset_layer_transformation_accessor";
import { setVersionNumberAction } from "./model/actions/save_actions";
import {
  convertBoundingBoxProtoToObject,
  convertServerAdditionalAxesToFrontEnd,
  convertServerAnnotationToFrontendAnnotation,
} from "./model/reducers/reducer_helpers";

export const HANDLED_ERROR = "error_was_handled";
type DataLayerCollection = Record<string, DataLayer>;

export async function initialize(
  initialMaybeCompoundType: APICompoundType | null,
  initialCommandType: TraceOrViewCommand,
  initialFetch: boolean,
  version?: number | undefined | null,
): Promise<
  | {
      dataLayers: DataLayerCollection;
      maximumTextureCountForLayer: number;
    }
  | null
  | undefined
> {
  Store.dispatch(setControlModeAction(initialCommandType.type));
  let annotation: APIAnnotation | null | undefined;
  let annotationProto: APITracingStoreAnnotation | null | undefined;
  let userState: APIAnnotationUserState | null | undefined;
  let datasetId: string;

  if (initialCommandType.type === ControlModeEnum.TRACE) {
    const { annotationId } = initialCommandType;
    if (initialMaybeCompoundType != null) {
      annotation = await getAnnotationCompoundInformation(annotationId, initialMaybeCompoundType);
    } else {
      let unversionedAnnotation = await getUnversionedAnnotationInformation(annotationId);
      annotationProto = await getAnnotationProto(
        unversionedAnnotation.tracingStore.url,
        unversionedAnnotation.id,
        version,
      );
      userState =
        annotationProto != null
          ? getUserStateForTracing(annotationProto, Store.getState().activeUser, annotation?.owner)
          : null;
      const layersWithStats = annotationProto.annotationLayers.map((protoLayer) => {
        return {
          tracingId: protoLayer.tracingId,
          name: protoLayer.name,
          typ: protoLayer.typ,
          stats:
            // Only when the newest version is requested (version==null),
            // the stats are available in unversionedAnnotation.
            version == null
              ? (unversionedAnnotation.annotationLayers.find(
                  (layer) => layer.tracingId === protoLayer.tracingId,
                )?.stats ?? {})
              : {},
        };
      });
      const completeAnnotation = {
        ...unversionedAnnotation,
        description: annotationProto.description,
        annotationProto: annotationProto.earliestAccessibleVersion,
        annotationLayers: layersWithStats,
      };
      annotation = completeAnnotation;
    }
    datasetId = annotation.datasetId;

    if (!annotation.restrictions.allowAccess) {
      Toast.error(messages["tracing.no_access"]);
      throw HANDLED_ERROR;
    }

    ErrorHandling.assertExtendContext({
      task: annotation.id,
    });
    Store.dispatch(setTaskAction(annotation.task));
  } else if (initialCommandType.type === ControlModeEnum.SANDBOX) {
    datasetId = initialCommandType.datasetId;
    annotation = await getEmptySandboxAnnotationInformation(
      datasetId,
      initialCommandType.tracingType,
      getSharingTokenFromUrlParameters(),
    );
  } else {
    datasetId = initialCommandType.datasetId;
  }

  const [apiDataset, initialUserSettings, serverTracings, keyboardShortcutsConfig] =
    await fetchParallel(annotation, datasetId, version);
  assertUsableDataset(apiDataset as StoreDataset, initialCommandType);
  maybeFixDatasetNameInURL(apiDataset, initialCommandType);

  const serverVolumeTracings = getServerVolumeTracings(serverTracings);
  const serverVolumeTracingIds = serverVolumeTracings.map((volumeTracing) => volumeTracing.id);
  const dataset = preprocessDataset(apiDataset, serverTracings);
  initializeDataset(initialFetch, dataset);
  const initialDatasetSettings = await getDatasetViewConfiguration(
    dataset,
    serverVolumeTracingIds,
    getSharingTokenFromUrlParameters(),
  );
  if (annotation?.viewConfiguration) {
    const isPartial = true;
    enforceValidatedDatasetViewConfiguration(annotation?.viewConfiguration, dataset, isPartial);
  }
  const annotationSpecificDatasetSettings = applyAnnotationSpecificViewConfiguration(
    annotation,
    dataset,
    initialDatasetSettings,
  );
  const migratedUserSettings = migrateUserConfiguration(initialUserSettings);
  const enforcedInitialUserSettings =
    enforcePricingRestrictionsOnUserConfiguration(migratedUserSettings);
  initializeSettings(
    enforcedInitialUserSettings,
    annotationSpecificDatasetSettings,
    initialDatasetSettings,
  );

  // Load keyboard shortcuts from backend. Schema errors (malformed values) are
  // warned about but don't discard the whole config — sanitizeKeyboardShortcuts
  // keeps each valid entry and falls back to the default for malformed ones.
  // Unknown keys (from newer clients) are dropped; missing keys (from older
  // saved configs) are filled in with defaults.
  const { valid, parsed, errors } = validateShortcutMapText(
    JSON.stringify(keyboardShortcutsConfig),
  );
  if (!valid && !isEmpty(keyboardShortcutsConfig)) {
    Toast.warning(messages["users.failed_parsing_keyboard_shortcuts_config"]);
    console.warn("Found errors while parsing user's keyboard shortcut configurations:", errors);
  }
  const shortcuts: KeyboardShortcutsMap =
    parsed != null ? sanitizeKeyboardShortcuts(parsed) : getAllDefaultKeyboardShortcuts();
  Store.dispatch(setKeyboardShortcutsConfigAction(shortcuts));
  initializeKeyboardLayoutMap();

  let initializationInformation = null;

  // There is no need to reinstantiate the DataLayers if the dataset didn't change.
  if (initialFetch) {
    const { gpuMemoryFactor } = Store.getState().userConfiguration;
    initializationInformation = initializeDataLayerInstances(gpuMemoryFactor);
    if (userState != null) {
      Store.dispatch(setZoomStepAction(userState.zoomLevel));
    }
    const { smallestCommonBucketCapacity, maximumLayerCountToRender } = initializationInformation;
    Store.dispatch(
      initializeGpuSetupAction(
        smallestCommonBucketCapacity,
        gpuMemoryFactor,
        maximumLayerCountToRender,
      ),
    );
  }

  // There is no need to initialize the annotation if there is no annotation (View mode).
  if (annotation != null) {
    const editableMappings = await fetchEditableMappings(
      annotation.tracingStore.url,
      serverVolumeTracings,
      annotation.id,
      version,
    );
    initializeAnnotation(
      annotation,
      annotationProto?.version ?? 1,
      annotationProto?.earliestAccessibleVersion ?? 0,
      serverTracings,
      editableMappings,
    );
  } else {
    // In dataset view mode (no annotation) we need to set the view mode too.
    const { allowedModes } = determineAllowedModes();
    const mode = UrlManager.initialState.mode || allowedModes[0];
    Store.dispatch(setViewModeAction(mode));
  }

  const defaultState = determineDefaultState(UrlManager.initialState, userState, serverTracings);
  // Don't override zoom when swapping the task
  applyState(defaultState, !initialFetch, dataset);

  if (initialFetch) {
    setInitialTool();
  }

  return initializationInformation;
}

function maybeFixDatasetNameInURL(dataset: APIDataset, initialCommandType: TraceOrViewCommand) {
  if (
    initialCommandType.type === ControlModeEnum.VIEW ||
    initialCommandType.type === ControlModeEnum.SANDBOX
  ) {
    const datasetNameInURL = getDatasetNameFromLocation(location);
    if (dataset.name !== datasetNameInURL) {
      const pathnameWithUpdatedDatasetName = getUpdatedPathnameWithNewDatasetName(
        location,
        dataset,
      );
      UrlManager.changeBaseUrl(pathnameWithUpdatedDatasetName + location.search);
    }
  }
}

async function fetchParallel(
  annotation: APIAnnotation | null | undefined,
  datasetId: string,
  version: number | undefined | null,
): Promise<[APIDataset, UserConfiguration, Array<ServerTracing>, Partial<KeyboardShortcutsMap>]> {
  return Promise.all([
    getDataset(datasetId, getSharingTokenFromUrlParameters()),
    getUserConfiguration(), // Fetch the actual tracing from the datastore, if there is an skeletonAnnotation
    annotation ? getTracingsForAnnotation(annotation, version) : [],
    getKeyboardShortcutsConfig(),
  ]);
}

async function fetchEditableMappings(
  tracingStoreUrl: string,
  serverVolumeTracings: ServerVolumeTracing[],
  annotationId: string,
  version: number | undefined | null,
): Promise<ServerEditableMapping[]> {
  const promises = serverVolumeTracings
    .filter((tracing) => tracing.hasEditableMapping)
    .map((tracing) => getEditableMappingInfo(tracingStoreUrl, tracing.id, annotationId, version));
  return Promise.all(promises);
}

function validateSpecsForLayers(dataset: StoreDataset, requiredBucketCapacity: number): any {
  const layers = dataset.dataSource.dataLayers;
  const specs = getSupportedTextureSpecs();
  validateMinimumRequirements(specs);
  const setupDetails = computeDataTexturesSetup(
    specs,
    layers,
    hasSegmentation(dataset),
    requiredBucketCapacity,
  );

  maybeWarnAboutUnsupportedLayers(layers);
  return setupDetails;
}

function maybeWarnAboutUnsupportedLayers(layers: Array<APIDataLayer>): void {
  for (const layer of layers) {
    if (!isElementClassSupported(layer)) {
      Toast.warning(messages["dataset.unsupported_element_class"](layer.name, layer.elementClass), {
        sticky: true,
      });
    } else if (layer.category === "segmentation" && layer.elementClass === "uint24") {
      Toast.error(messages["dataset.unsupported_segmentation_class_uint24"]);
    }
  }
}

function initializeAnnotation(
  _annotation: APIAnnotation,
  version: number,
  earliestAccessibleVersion: number,
  serverTracings: Array<ServerTracing>,
  editableMappings: Array<ServerEditableMapping>,
) {
  // This method is not called for the View mode
  const { dataset, save: saveState } = Store.getState();
  let annotation = _annotation;
  const { allowedModes, preferredMode } = determineAllowedModes(annotation.settings);

  extend(annotation.settings, {
    allowedModes,
    preferredMode,
  });

  const { controlMode } = Store.getState().temporaryConfiguration;

  if (controlMode !== ControlModeEnum.VIEW) {
    if (controlMode === ControlModeEnum.SANDBOX) {
      annotation = {
        ...annotation,
        restrictions: {
          ...annotation.restrictions,
          allowUpdate: true,
          allowSave: false,
        },
      };
    } else if (controlMode === ControlModeEnum.TRACE) {
      annotation = {
        ...annotation,
        restrictions: {
          ...annotation.restrictions,
          // If saving was disabled at some point, we don't want this to ever be reset.
          // For example, when switching between versions in the version restore view,
          // api.tracing.restart is called which calls Model.fetch here.
          allowSave: annotation.restrictions.allowUpdate && !saveState.isSavingDisabled,
        },
      };
    }

    const initializationActions = [];
    initializationActions.push(
      initializeAnnotationAction(
        convertServerAnnotationToFrontendAnnotation(annotation, version, earliestAccessibleVersion),
      ),
    );
    getServerVolumeTracings(serverTracings).forEach((volumeTracing) => {
      ErrorHandling.assert(
        getSegmentationLayers(dataset).length > 0,
        messages["tracing.volume_missing_segmentation"],
      );
      initializationActions.push(initializeVolumeTracingAction(volumeTracing));
    });

    editableMappings.map((mapping) =>
      initializationActions.push(initializeEditableMappingAction(mapping)),
    );

    const skeletonTracing = getNullableSkeletonTracing(serverTracings);

    if (skeletonTracing != null) {
      initializationActions.push(initializeSkeletonTracingAction(skeletonTracing));
    }

    Store.dispatch(batchedAnnotationInitializationAction(initializationActions));
    Store.dispatch(setVersionNumberAction(version));
  }

  // Initialize 'orthogonal' or 'flight' mode
  if (allowedModes.length === 0) {
    Toast.warning(messages["tracing.no_allowed_mode"]);
  }
  const maybeUrlViewMode = UrlManager.initialState.mode;
  const mode = preferredMode || maybeUrlViewMode || allowedModes[0] || "orthogonal";
  Store.dispatch(setViewModeAction(mode));
}

function setInitialTool() {
  const { useLegacyBindings } = Store.getState().userConfiguration;

  if (!useLegacyBindings) {
    // The MOVE tool is already the default
    return;
  }

  const { annotation } = Store.getState();

  if (annotation.skeleton != null) {
    // We are in a annotation which contains a skeleton. Due to the
    // enabled legacy-bindings, the user can expect to immediately create new nodes
    // with right click. Therefore, switch to the skeleton tool.
    Store.dispatch(setToolAction(AnnotationTool.SKELETON));
  }
}

export function preprocessDataset(
  dataset: APIDataset,
  serverTracings: Array<ServerTracing>,
): StoreDataset {
  const mutableDataset = dataset as any as MutableAPIDataset;
  const volumeTracings = getServerVolumeTracings(serverTracings);

  if (volumeTracings.length > 0) {
    const newDataLayers = getMergedDataLayersFromDatasetAndVolumeTracings(dataset, volumeTracings);
    mutableDataset.dataSource.dataLayers = newDataLayers;
    validateVolumeLayers(volumeTracings, newDataLayers);
  }

  (mutableDataset as StoreDataset).areLayersPreprocessed = true;

  return mutableDataset as StoreDataset;
}

function assertUsableDataset(dataset: StoreDataset, initialCommandType: TraceOrViewCommand) {
  let error;
  let annotationNote = "";
  if (initialCommandType.type === ControlModeEnum.TRACE) {
    annotationNote = `Failed to load annotation ${initialCommandType.annotationId}: `;
  }

  if (!dataset) {
    error = `${annotationNote}${messages["dataset.does_not_exist"]}`;
  } else if (!dataset.dataSource.dataLayers) {
    let statusNote = ".";
    if (dataset.dataSource.status) {
      statusNote = `: ${dataset.dataSource.status}`;
    }
    error = `${annotationNote}Dataset ‘${dataset.name}’ (${dataset.id}) is not available${statusNote}`;
  }

  if (error) {
    Toast.error(error);
    throw HANDLED_ERROR;
  }
}

function initializeDataset(initialFetch: boolean, dataset: StoreDataset): void {
  // Make sure subsequent fetch calls are always for the same dataset
  if (!initialFetch) {
    ErrorHandling.assert(
      isEqual(dataset.id, Store.getState().dataset.id),
      messages["dataset.changed_without_reload"],
    );
  }

  ErrorHandling.assertExtendContext({
    datasetName: dataset.name,
    datasetId: dataset.id,
  });

  Store.dispatch(setDatasetAction(dataset));
  initializeAdditionalCoordinates(dataset);
}

function initializeAdditionalCoordinates(dataset: StoreDataset) {
  const unifiedAdditionalCoordinates = getUnifiedAdditionalCoordinates(dataset);
  const initialAdditionalCoordinates = Object.values(unifiedAdditionalCoordinates).map(
    ({ name, bounds }) => ({
      name,
      value: Math.floor((bounds[1] - bounds[0]) / 2),
    }),
  );

  Store.dispatch(setAdditionalCoordinatesAction(initialAdditionalCoordinates));
}

function initializeSettings(
  initialUserSettings: UserConfiguration,
  initialDatasetSettings: DatasetConfiguration,
  originalDatasetSettings: DatasetConfiguration,
): void {
  Store.dispatch(
    initializeSettingsAction(initialUserSettings, initialDatasetSettings, originalDatasetSettings),
  );
}

function initializeDataLayerInstances(gpuFactor: number | null | undefined): {
  dataLayers: DataLayerCollection;
  maximumTextureCountForLayer: number;
  smallestCommonBucketCapacity: number;
  maximumLayerCountToRender: number;
} {
  const { dataset } = Store.getState();
  const requiredBucketCapacity =
    constants.GPU_FACTOR_MULTIPLIER *
    (gpuFactor != null ? gpuFactor : constants.DEFAULT_GPU_MEMORY_FACTOR);
  const {
    textureInformationPerLayer,
    smallestCommonBucketCapacity,
    maximumLayerCountToRender,
    maximumTextureCountForLayer,
  } = validateSpecsForLayers(dataset, requiredBucketCapacity);

  if (import.meta.env.MODE !== "test") {
    console.log("Supporting", smallestCommonBucketCapacity, "buckets");
  }

  const layers = dataset.dataSource.dataLayers;
  const dataLayers: DataLayerCollection = {};

  for (const layer of layers) {
    const textureInformation = textureInformationPerLayer.get(layer);

    if (!textureInformation) {
      throw new Error("No texture information for layer?");
    }

    dataLayers[layer.name] = new DataLayer(
      layer,
      textureInformation.textureSize,
      textureInformation.textureCount,
      layer.name, // In case of a volume tracing layer the layer name will equal its tracingId.
    );
  }

  if (getDataLayers(dataset).length === 0) {
    Toast.error(messages["dataset.no_data"]);
    throw HANDLED_ERROR;
  }

  return {
    dataLayers,
    maximumTextureCountForLayer,
    smallestCommonBucketCapacity,
    maximumLayerCountToRender,
  };
}

function getMergedDataLayersFromDatasetAndVolumeTracings(
  dataset: APIDataset,
  tracings: Array<ServerVolumeTracing>,
): Array<APIDataLayer> {
  // This method adds/merges the segmentation layers of the tracing into the dataset layers.

  const originalLayers = dataset.dataSource.dataLayers;
  const newLayers = originalLayers.slice();
  const allLayersSameRotation = doAllLayersHaveTheSameRotation(originalLayers);

  for (const tracing of tracings) {
    // The tracing always contains the layer information for the user segmentation.
    // Two possible cases:
    // 1) The volume layer should not be based on an existing layer. In that case, fallbackLayer is undefined
    //    and a new layer is created and added.
    // 2) The volume layer should be based on a fallback layer. In that case, merge the original fallbackLayer
    //    with the new volume layer.
    const fallbackLayerIndex = originalLayers.findIndex(
      (layer) => layer.name === tracing.fallbackLayer,
    );

    const fallbackLayer = fallbackLayerIndex > -1 ? originalLayers[fallbackLayerIndex] : null;
    const mags = tracing.mags || [];
    const tracingHasMagList = mags.length > 0;
    let coordinateTransformsMaybe = {};
    if (allLayersSameRotation) {
      coordinateTransformsMaybe = {
        coordinateTransformations: originalLayers?.[0].coordinateTransformations,
      };
    } else if (fallbackLayer?.coordinateTransformations) {
      coordinateTransformsMaybe = {
        coordinateTransformations: fallbackLayer.coordinateTransformations,
      };
    }
    // Legacy tracings don't have the `tracing.mags` property
    // since they were created before WK started to maintain multiple magnifications
    // in volume annotations. Therefore, this code falls back to mag (1, 1, 1) for
    // that case.
    const tracingMags: { mag: Vector3 }[] = tracingHasMagList
      ? mags.map(({ x, y, z }) => ({ mag: [x, y, z] }))
      : [{ mag: [1, 1, 1] }];
    const tracingLayer: APISegmentationLayer = {
      name: tracing.id,
      tracingId: tracing.id,
      elementClass: tracing.elementClass,
      category: "segmentation",
      largestSegmentId: tracing.largestSegmentId,
      boundingBox: convertBoundingBoxProtoToObject(tracing.boundingBox),
      mags: tracingMags,
      mappings:
        fallbackLayer != null && "mappings" in fallbackLayer ? fallbackLayer.mappings : undefined,
      // Remember the name of the original layer (e.g., used to request mappings)
      fallbackLayer: tracing.fallbackLayer,
      fallbackLayerInfo: fallbackLayer,
      additionalAxes: convertServerAdditionalAxesToFrontEnd(tracing.additionalAxes),
      ...coordinateTransformsMaybe,
    };
    if (fallbackLayerIndex > -1) {
      newLayers[fallbackLayerIndex] = tracingLayer;
    } else {
      newLayers.push(tracingLayer);
    }
  }

  return newLayers;
}

function validateVolumeLayers(
  volumeTracings: Array<ServerVolumeTracing>,
  dataLayers: Array<APIDataLayer>,
) {
  /*
   * Validate that every volume tracing got a corresponding data layer.
   */
  const layersForVolumeTracings = volumeTracings.map((volumeTracing) =>
    dataLayers.find(
      (layer) => layer.category === "segmentation" && layer.tracingId === volumeTracing.id,
    ),
  );

  if (layersForVolumeTracings.some((layer) => layer == null)) {
    throw new Error(
      "Initialization of volume tracing layers didn't succeed. Not all volume tracings have a corresponding data layer.",
    );
  }
}

function determineDefaultState(
  urlState: PartialUrlManagerState,
  userState: APIAnnotationUserState | null | undefined,
  tracings: Array<ServerTracing>,
): PartialUrlManagerState {
  const {
    position: urlStatePosition,
    additionalCoordinates: urlStateAdditionalCoordinates,
    zoomStep: urlStateZoomStep,
    rotation: urlStateRotation,
    activeNode: urlStateActiveNode,
    stateByLayer: urlStateByLayer,
    ...rest
  } = urlState;
  // If there is no editPosition (e.g. when viewing a dataset) and
  // no default position, compute the center of the dataset
  const { dataset, datasetConfiguration } = Store.getState();
  const defaultPosition = datasetConfiguration.position;
  let position = getDatasetCenter(dataset);
  let additionalCoordinates = null;

  // someTracing should only be used if no userState exists (this is the case
  // for annotations that were not touched after #8542 was deployed).
  const someTracing = first(tracings);

  if (defaultPosition != null) {
    position = defaultPosition;
  }

  if (userState) {
    position = point3ToVector3(userState.editPosition);
    additionalCoordinates = userState.editPositionAdditionalCoordinates;
  } else if (someTracing != null) {
    position = point3ToVector3(someTracing.editPosition);
    additionalCoordinates = someTracing.editPositionAdditionalCoordinates;
  }

  if (urlStatePosition != null) {
    position = urlStatePosition;
  }

  if (urlStateAdditionalCoordinates != null) {
    additionalCoordinates = urlStateAdditionalCoordinates;
  }

  let zoomStep = datasetConfiguration.zoom;

  if (urlStateZoomStep != null) {
    zoomStep = urlStateZoomStep;
  } else if (userState != null) {
    zoomStep = userState.zoomLevel;
  } else if (someTracing != null) {
    zoomStep = someTracing.zoomLevel;
  }

  let rotation = datasetConfiguration.rotation;
  if (userState != null) {
    rotation = point3ToVector3(userState.editRotation);
  } else if (someTracing != null) {
    rotation = point3ToVector3(someTracing.editRotation);
  }

  if (urlStateRotation != null) {
    rotation = urlStateRotation;
  }

  const stateByLayer = urlStateByLayer ?? {};
  // Add the default mapping to the state for each layer that does not have a mapping set in its URL settings.
  for (const layerName in datasetConfiguration.layers) {
    if (!(layerName in stateByLayer)) {
      stateByLayer[layerName] = {};
    }
    const { mapping } = datasetConfiguration.layers[layerName];
    if (stateByLayer[layerName].mappingInfo == null && mapping != null) {
      stateByLayer[layerName].mappingInfo = {
        mappingName: mapping.name,
        mappingType: mapping.type,
      };
    }
  }

  // Overwriting the mapping to load for each volume layer in case
  // - the volume tracing has a not locked mapping set and the url does not.
  // - the volume tracing has a locked mapping set.
  // - the volume tracing has locked that no tracing should be loaded.
  const volumeTracings = tracings.filter(
    (tracing) => tracing.typ === "Volume",
  ) as ServerVolumeTracing[];
  for (const volumeTracing of volumeTracings) {
    const { id: layerName, mappingName, mappingIsLocked } = volumeTracing;

    if (!(layerName in stateByLayer)) {
      stateByLayer[layerName] = {};
    }
    if (stateByLayer[layerName].mappingInfo == null || mappingIsLocked) {
      // A locked mapping always takes precedence over the URL configuration.
      if (mappingName == null) {
        delete stateByLayer[layerName].mappingInfo;
      } else {
        stateByLayer[layerName].mappingInfo = {
          mappingName,
          mappingType: "HDF5",
        };
      }
    }
  }

  const activeNode = urlStateActiveNode;
  return {
    position,
    zoomStep,
    rotation,
    activeNode,
    stateByLayer,
    additionalCoordinates,
    ...rest,
  };
}

function migrateUserConfiguration(userConfiguration: UserConfiguration): UserConfiguration {
  // clippingDistanceArbitrary was renamed to clippingDistanceFlight. Carry over the stored
  // value so users don't silently lose their saved setting after the upgrade.
  const { clippingDistanceArbitrary, clippingDistanceFlight, ...remainingConfig } =
    userConfiguration as UserConfiguration & { clippingDistanceArbitrary?: number };
  if (clippingDistanceArbitrary != null && clippingDistanceFlight == null) {
    return { ...remainingConfig, clippingDistanceFlight: clippingDistanceArbitrary as number };
  }
  return userConfiguration;
}

function enforcePricingRestrictionsOnUserConfiguration(
  userConfiguration: UserConfiguration,
): UserConfiguration {
  const activeOrganization = Store.getState().activeOrganization;
  if (!isFeatureAllowedByPricingPlan(activeOrganization, PricingPlanEnum.Team)) {
    return {
      ...userConfiguration,
      renderWatermark: true,
    };
  }
  return userConfiguration;
}

function applyAnnotationSpecificViewConfiguration(
  annotation: APIAnnotation | null | undefined,
  dataset: StoreDataset,
  originalDatasetSettings: DatasetConfiguration,
): DatasetConfiguration {
  /**
   * Apply annotation-specific view configurations to the dataset settings which are persisted
   * per user per dataset. The AnnotationViewConfiguration currently only holds the "isDisabled"
   * information per layer which should override the isDisabled information in DatasetConfiguration.
   * Moreover, due to another annotation nativelyRenderedLayerName might be set to a layer which does
   * not exist in this view / annotation. In this case, the nativelyRenderedLayerName should be set to null.
   */

  const initialDatasetSettings: Mutable<DatasetConfiguration> = cloneDeep(originalDatasetSettings);
  if (!annotation) {
    return initialDatasetSettings;
  }

  if (annotation.viewConfiguration) {
    // The annotation already contains a user specific viewConfiguration. Merge that into the
    // dataset settings.
    merge(initialDatasetSettings, annotation.viewConfiguration);
  }

  // Ensure configured nativelyRenderedLayerName is actually present in the dataset.
  if (initialDatasetSettings.nativelyRenderedLayerName) {
    const isNativelyRenderedNamePresent = getIsNativelyRenderedNamePresent(
      dataset,
      initialDatasetSettings.nativelyRenderedLayerName,
      annotation,
    );
    if (!isNativelyRenderedNamePresent) {
      initialDatasetSettings.nativelyRenderedLayerName = null;
    }
  }

  if (annotation.viewConfiguration) {
    // If the annotation had a view configuration it is now applied and nativelyRenderedLayerName has a correct value.
    return initialDatasetSettings;
  }

  // The annotation does not contain a viewConfiguration (mainly happens when the
  // annotation was opened for the very first time).
  // Make the first volume layer visible and turn the other segmentation layers invisible,
  // since only one segmentation layer can be visible currently.
  const firstVolumeLayer = first(
    annotation.annotationLayers.filter((layer) => layer.typ === "Volume"),
  );
  if (!firstVolumeLayer) {
    // No volume layer exists. Return the original dataset settings
    return initialDatasetSettings;
  }

  const newLayers: Record<string, DatasetLayerConfiguration> = {};
  for (const layerName of Object.keys(initialDatasetSettings.layers)) {
    if (isSegmentationLayer(dataset, layerName)) {
      const shouldBeDisabled = firstVolumeLayer.tracingId !== layerName;

      newLayers[layerName] = {
        ...initialDatasetSettings.layers[layerName],
        isDisabled: shouldBeDisabled,
      };
    } else {
      newLayers[layerName] = initialDatasetSettings.layers[layerName];
    }
  }

  return {
    ...initialDatasetSettings,
    layers: newLayers,
  };
}
